import { useCallback, useEffect, useRef } from 'react'
import { Box, InfiniteScroll } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { DndProvider } from 'react-dnd-multi-backend'
import { HTML5toTouch } from 'rdndmb-html5-to-touch'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useTimeGridLayout } from 'modules/shift_management/hooks/gridTable/useTimeGridLayout'
import { useGridSelection } from 'modules/shift_management/hooks/gridTable/selection/useGridSelection'
import { useGridClipboard } from 'modules/shift_management/hooks/gridTable/useGridClipboard'
import { useClearSelectionOnOutsidePress } from 'modules/shift_management/hooks/gridTable/useClearSelectionOnOutsidePress'
import TimeGridHeader from './components/TimeGridHeader'
import type {
  TimeGridEntity,
  TimeGridEvent,
  TimeGridTableProps,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { GridVirtualization } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/GridVirtualization'

const TimeGridTable = <TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop>({
  entities,
  events,
  timeRange,
  granularity,
  rowHeaderLabel,
  rowHeaderWidth,
  minColumnWidth,
  rowMinHeight,
  paddingX,
  paddingY,
  cardLayout,
  showGridLines = true,
  stickyHeaders = true,
  enableHorizontalScroll = true,
  enableRowVirtualization = true,
  preloadedRowsOutsideViewport = 10,
  timeSlotFormatter,
  isCellSelectable,
  isCellDisabled,
  rowHeaderRenderer,
  eventRenderer,
  cardAppearance,
  secondaryLayerRenderer,
  rowActionsRenderer,
  dragDropHandlers,
  dragAndDropAdapter,
  clipboardHandlers,
  onSelectionChange,
  onAddShiftClick,
  ghostCells,
  ghostAnchorCell,
  onGhostCardRef,
  children,
}: TimeGridTableProps<TEntity, TEvent, TDragDrop>) => {
  const [{ page, hasMore, isLoading }, dispatchPagination] = usePagination()
  const internalScrollRef = useRef<HTMLDivElement>(null)
  const copySelectionRef = useRef<(() => void) | null>(null)
  const loadMore = () => {
    if (isLoading) return
    dispatchPagination({ type: 'NEXT_PAGE' })
  }

  const { rows, slots, rowByEntityId, rowIndexByEntityId } = useTimeGridLayout({
    entities,
    events,
    timeRange,
    granularity,
  })

  const {
    selection,
    selectedCellKeys,
    selectedRowIds,
    selectedColumnIndexes,
    selectedEventKeys,
    clearSelection,
    handleCellSelected,
    handleRowHeaderSelected,
    handleColumnHeaderSelected,
    handleHeaderDragStart,
    handleCornerCellSelected,
    isAllSelected,
    handleEventSelected,
    fromCellKey,
  } = useGridSelection({ rows, slots, rowIndexByEntityId, isCellSelectable })

  useEffect(() => {
    onSelectionChange?.(selection, slots)
  }, [selection, slots, onSelectionChange])

  useGridClipboard({
    layout: { rows, slots, rowByEntityId, rowIndexByEntityId },
    selection: {
      selection,
      fromCellKey,
    },
    clipboardHandlers,
    clearSelection,
    onCopyReady: (onCopy) => {
      copySelectionRef.current = onCopy
    },
  })

  useClearSelectionOnOutsidePress({
    rootRef: internalScrollRef,
    onClearSelection: clearSelection,
  })

  const handleAddShiftClick = useCallback(
    (entity: TEntity, slotIndex: number, anchorRect: DOMRect) => {
      onAddShiftClick?.(entity, slotIndex, anchorRect)
    },
    [onAddShiftClick]
  )

  const gridMinWidth = rowHeaderWidth + minColumnWidth * slots.length

  const content = (
    <Box
      role="grid"
      style={{
        width: '100%',
        overflowX: enableHorizontalScroll ? 'auto' : 'hidden',
        overflowY: 'overlay',
      }}
      height="full-view-height"
      ref={internalScrollRef}
    >
      <TimeGridHeader
        rowHeaderLabel={rowHeaderLabel}
        rowHeaderWidth={rowHeaderWidth}
        minColumnWidth={minColumnWidth}
        gridMinWidth={gridMinWidth}
        showGridLines={showGridLines}
        stickyHeaders={stickyHeaders}
        granularity={granularity}
        slots={slots}
        timeSlotFormatter={timeSlotFormatter}
        selectedColumnIndexes={selectedColumnIndexes}
        isAllSelected={isAllSelected}
        onColumnHeaderSelected={handleColumnHeaderSelected}
        onColumnHeaderDragStart={handleHeaderDragStart}
        onCornerCellSelected={handleCornerCellSelected}
      />
      <InfiniteScroll
        threshold={800}
        pageStart={page}
        loadMore={loadMore}
        hasMore={hasMore}
        useWindow={false}
        getScrollParent={() => internalScrollRef.current}
        style={{
          minWidth: 'min-content',
          flex: '1 0 auto',
          zIndex: 0,
          position: 'relative',
        }}
        loader={
          <Box alignItems="center" width="full" padding="s32" key="loading" flexGrow>
            <Spinner />
          </Box>
        }
      >
        <GridVirtualization
          rows={rows}
          rowMinHeight={rowMinHeight}
          scrollContainerRef={internalScrollRef}
          preloadedRowsOutsideViewport={preloadedRowsOutsideViewport}
          enableRowVirtualization={enableRowVirtualization}
          gridMinWidth={gridMinWidth}
          rowHeaderRenderer={rowHeaderRenderer}
          eventRenderer={eventRenderer}
          cardAppearance={cardAppearance}
          secondaryLayerRenderer={secondaryLayerRenderer}
          rowActionsRenderer={rowActionsRenderer}
          dragDropHandlers={dragDropHandlers}
          dragAndDropAdapter={dragAndDropAdapter}
          rowHeaderWidth={rowHeaderWidth}
          minColumnWidth={minColumnWidth}
          paddingX={paddingX}
          paddingY={paddingY}
          cardLayout={cardLayout}
          showGridLines={showGridLines}
          slots={slots}
          isCellSelectable={isCellSelectable}
          isCellDisabled={isCellDisabled}
          onCellSelected={handleCellSelected}
          selectedCellKeys={selectedCellKeys}
          selectedRowIds={selectedRowIds}
          selectedColumnIndexes={selectedColumnIndexes}
          selectedEventKeys={selectedEventKeys}
          isAllSelected={isAllSelected}
          onEventSelected={handleEventSelected}
          onCopySelection={() => copySelectionRef.current?.()}
          onRowHeaderSelected={handleRowHeaderSelected}
          onHeaderDragStart={handleHeaderDragStart}
          onAddShiftClick={handleAddShiftClick}
          ghostCells={ghostCells}
          ghostAnchorCell={ghostAnchorCell}
          onGhostCardRef={onGhostCardRef}
        />
      </InfiniteScroll>
      {children}
    </Box>
  )

  return (
    <DndProvider options={HTML5toTouch} context={window}>
      {content}
    </DndProvider>
  )
}

export { TimeGridTable }
